// features/destinations/destinationsSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getCompanyCurrencyCode } from "../../utils/companyCurrency";
const BASE_URL = process.env.REACT_APP_CLIENT_API_URL;

const getNonAuthHeaders = () => {
  let lang = localStorage.getItem("lang");
  return {
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": lang,
    },
  };
};

// Async thunk: fetchDestinations performs the API request for this slice.
export const fetchDestinations = createAsyncThunk(
  "destinations/fetchDestinations",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL + "/getDestinations",
        {
          lang_code: params.lang_code,
          currency_code: getCompanyCurrencyCode(),
          country_code: "",
          leaf: params.leaf,
          trip_type: 0,
          parent_id: params.parent_id
        },
        getNonAuthHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Async thunk: fetchDestChildren performs the API request for this slice.
export const fetchDestChildren = createAsyncThunk(
  "destinations/fetchDestChildren",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL + "/getDestinations",
        {
          lang_code: params.lang_code,
          currency_code: getCompanyCurrencyCode(),
          country_code: "",
          leaf: params.leaf,
          trip_type: 0,
          parent_id: params.parent_id
        },
        getNonAuthHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Async thunk: fetchDestinationTree performs the API request for this slice.
export const fetchDestinationTree = createAsyncThunk(
  "destinations/fetchDestinationTree",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        BASE_URL + "/GetDestinationsWithTypes",
        null,
        {
          ...getNonAuthHeaders(),
          params: { lang: params.lang_code, categoryId: params.trip_category },
        }
      );
      return {
        data: response.data,
        trip_category: params.trip_category,
      };
    } catch (error) {
      return rejectWithValue({
        error: error.response.data,
        trip_category: params.trip_category,
      });
    }
  }
);

// Slice definition: contains local reducers and async thunk state transitions.
const destinationsSlice = createSlice({
  name: "destinations",
  initialState: {
    items: [],
    childrenItems:[],
    treeItems: {
      1: [], // Excursions
      2: [], // Transfers
      3: [], // Diving
    },
    loading: false,
    treeLoading: {
      1: false, // Excursions
      2: false, // Transfers
      3: false, // Diving
    },
    error: null,
    treeError: {
      1: null, // Excursions
      2: null, // Transfers
      3: null, // Diving
    },
  },
  reducers: {},
  // Async lifecycle reducers for pending/fulfilled/rejected thunk states.
  extraReducers: (builder) => {
    builder
      .addCase(fetchDestinations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDestinations.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchDestinations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchDestChildren.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDestChildren.fulfilled, (state, action) => {
        state.loading = false;
        state.childrenItems = action.payload;
      })
      .addCase(fetchDestChildren.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchDestinationTree.pending, (state, action) => {
        const tripCategory = action.meta.arg.trip_category;
        state.treeLoading[tripCategory] = true;
        state.treeError[tripCategory] = null;
      })
      .addCase(fetchDestinationTree.fulfilled, (state, action) => {
        const tripCategory = action.payload?.trip_category;
        state.treeLoading[tripCategory] = false;
        state.treeItems[tripCategory] = action.payload?.data;
      })
      .addCase(fetchDestinationTree.rejected, (state, action) => {
        const tripCategory = action.payload?.trip_category;
        state.treeLoading[tripCategory] = false;
        state.treeError[tripCategory] = action.payload?.error;
      });
  },
});

export default destinationsSlice.reducer;
